import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import { FiCopy, FiDownload, FiEye, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import {
    getAdminEmailByUuid,
    getAdminEmailEvents,
    getAdminEmailList,
    getAdminEmailStatistics,
} from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import {
    EMAIL_MONITOR_EVENT_TYPES,
    EMAIL_MONITOR_PAGE_SIZES,
    brevoEventToLabelKey,
    buildEmailMonitorExcelRows,
    fetchAllEmailMonitorItems,
    formatEmailMonitorDate,
    getDefaultEmailMonitorDateRange,
    getEmailEventBadgeVariant,
    getEmailMonitorHasNextPage,
    getEmailMonitorPageRange,
    isValidBrevoEventFilter,
    truncateEmailSubject,
    truncateEmailUuid,
    truncateMessageId,
    validateEmailMonitorListFilters,
} from '../../constants/emailMonitor';
import './EmailMonitorTab.css';

const defaultRange = getDefaultEmailMonitorDateRange();

const buildAppliedQuery = (overrides = {}) => ({
    dateFrom: defaultRange.date_from,
    dateTo: defaultRange.date_to,
    email: '',
    templateId: '',
    messageId: '',
    subject: '',
    eventType: '',
    page: 1,
    pageSize: 20,
    ...overrides,
});

const extractApiDetail = (error) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(' — ');
    }
    return null;
};

const EmailMonitorTab = ({ isActive = true }) => {
    const { t } = useTranslation();
    const prefix = 'pages.adminTripsDaily.emailMonitor';

    const [dateFrom, setDateFrom] = useState(defaultRange.date_from);
    const [dateTo, setDateTo] = useState(defaultRange.date_to);
    const [email, setEmail] = useState('');
    const [templateId, setTemplateId] = useState('');
    const [messageId, setMessageId] = useState('');
    const [subject, setSubject] = useState('');
    const [eventType, setEventType] = useState('');
    const [appliedQuery, setAppliedQuery] = useState(() => buildAppliedQuery());
    const [refreshNonce, setRefreshNonce] = useState(0);

    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState(null);

    const [items, setItems] = useState([]);
    const [listTotal, setListTotal] = useState(null);
    const [listHasMore, setListHasMore] = useState(null);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [filterValidationError, setFilterValidationError] = useState(null);

    const [showDetail, setShowDetail] = useState(false);
    const [detailContent, setDetailContent] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [detailEvents, setDetailEvents] = useState([]);
    const [copiedField, setCopiedField] = useState(null);

    const isEventListMode = Boolean(String(appliedQuery.eventType || '').trim());
    const isSubjectFilterMode = Boolean(String(appliedQuery.subject || '').trim());

    const resolveApiError = (error, fallback) => {
        if (error?.response?.status === 503) {
            return t(`${prefix}.errors.notConfigured`);
        }
        if (error?.response?.status === 502) {
            return t(`${prefix}.errors.brevoUnavailable`);
        }
        if (error?.response?.status === 403) {
            return t(`${prefix}.errors.forbidden`);
        }
        if (error?.response?.status === 422) {
            return extractApiDetail(error) || t(`${prefix}.errors.invalidFilters`);
        }
        return handleApiError(error, fallback);
    };

    useEffect(() => {
        if (!isActive) return;
        let cancelled = false;

        const fetchStats = async () => {
            setStatsLoading(true);
            setStatsError(null);
            try {
                const data = await getAdminEmailStatistics({
                    date_from: appliedQuery.dateFrom,
                    date_to: appliedQuery.dateTo,
                });
                if (!cancelled) setStats(data);
            } catch (error) {
                if (!cancelled) {
                    setStats(null);
                    setStatsError(resolveApiError(error, t(`${prefix}.errors.statsLoad`)));
                }
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        };

        const fetchList = async () => {
            setListLoading(true);
            setListError(null);
            try {
                const data = await getAdminEmailList({
                    page: appliedQuery.page,
                    page_size: appliedQuery.pageSize,
                    client_email: appliedQuery.email || undefined,
                    template_id: appliedQuery.templateId || undefined,
                    message_id: appliedQuery.messageId || undefined,
                    subject: appliedQuery.subject || undefined,
                    event_type: isSubjectFilterMode ? undefined : (appliedQuery.eventType || undefined),
                    date_from: appliedQuery.dateFrom || undefined,
                    date_to: appliedQuery.dateTo || undefined,
                });
                if (!cancelled) {
                    const batch = Array.isArray(data?.items) ? data.items : [];
                    setItems(batch);
                    setListTotal(isSubjectFilterMode ? null : (data?.total ?? null));
                    setListHasMore(data?.has_more ?? null);
                    if (batch.length === 0 && appliedQuery.page > 1) {
                        setAppliedQuery((prev) => ({
                            ...prev,
                            page: Math.max(1, prev.page - 1),
                        }));
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    setItems([]);
                    setListTotal(null);
                    setListHasMore(null);
                    setListError(resolveApiError(error, t(`${prefix}.errors.listLoad`)));
                }
            } finally {
                if (!cancelled) setListLoading(false);
            }
        };

        fetchStats();
        fetchList();

        return () => {
            cancelled = true;
        };
        // refreshNonce : rechargement manuel uniquement
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isActive,
        appliedQuery.dateFrom,
        appliedQuery.dateTo,
        appliedQuery.email,
        appliedQuery.templateId,
        appliedQuery.messageId,
        appliedQuery.subject,
        appliedQuery.eventType,
        appliedQuery.page,
        appliedQuery.pageSize,
        refreshNonce,
    ]);

    // Page vide alors que page > 1 (fin de liste dépassée) — reculer comme SMS
    useEffect(() => {
        if (!isActive || listLoading || listError) return;
        if (appliedQuery.page > 1 && items.length === 0) {
            setAppliedQuery((prev) => ({
                ...prev,
                page: Math.max(1, prev.page - 1),
            }));
        }
    }, [isActive, listLoading, listError, appliedQuery.page, items.length]);

    const handleApplyFilters = (e) => {
        e?.preventDefault();
        if (!isValidBrevoEventFilter(eventType)) {
            setFilterValidationError(t(`${prefix}.errors.invalidEvent`, { event: eventType }));
            return;
        }
        const validation = validateEmailMonitorListFilters({
            email: email.trim(),
            templateId: templateId.trim(),
            messageId: messageId.trim(),
            subject: subject.trim(),
            eventType,
        });
        if (!validation.valid) {
            setFilterValidationError(t(`${prefix}.errors.${validation.code}`));
            return;
        }
        setFilterValidationError(null);
        setAppliedQuery({
            dateFrom,
            dateTo,
            email: email.trim(),
            templateId: templateId.trim(),
            messageId: messageId.trim(),
            subject: subject.trim(),
            eventType: subject.trim() ? '' : eventType,
            page: 1,
            pageSize: appliedQuery.pageSize,
        });
    };

    const handleResetFilters = () => {
        const range = getDefaultEmailMonitorDateRange();
        setDateFrom(range.date_from);
        setDateTo(range.date_to);
        setEmail('');
        setTemplateId('');
        setMessageId('');
        setSubject('');
        setEventType('');
        setFilterValidationError(null);
        setAppliedQuery(buildAppliedQuery());
    };

    const handleRefresh = () => {
        setRefreshNonce((n) => n + 1);
    };

    const handleExportExcel = async () => {
        setExportLoading(true);
        setExportError(null);
        try {
            const allItems = await fetchAllEmailMonitorItems(appliedQuery, getAdminEmailList);
            const rows = buildEmailMonitorExcelRows(allItems, { isEventMode: isEventListMode });
            if (rows.length === 0) {
                setExportError(t(`${prefix}.exportNoData`));
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Emails Brevo');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
            XLSX.writeFile(workbook, `email_monitor_export_${stamp}.xlsx`);
        } catch (error) {
            setExportError(resolveApiError(error, t(`${prefix}.exportError`)));
        } finally {
            setExportLoading(false);
        }
    };

    const hasNextPage = getEmailMonitorHasNextPage({
        items,
        page: appliedQuery.page,
        pageSize: appliedQuery.pageSize,
        total: listTotal,
        hasMore: listHasMore,
        isEventListMode,
        isSubjectFilterMode,
    });

    const pageRange = getEmailMonitorPageRange({
        page: appliedQuery.page,
        pageSize: appliedQuery.pageSize,
        total: isSubjectFilterMode ? null : listTotal,
        itemCount: items.length,
    });

    const handleCopy = async (text, fieldKey) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldKey);
            setTimeout(() => setCopiedField(null), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedField(fieldKey);
            setTimeout(() => setCopiedField(null), 2000);
        }
    };

    const openDetail = async (item) => {
        if (!item?.uuid) return;
        setShowDetail(true);
        setDetailContent(item);
        setDetailError(null);
        setDetailEvents([]);
        setDetailLoading(true);
        try {
            const [fresh, eventsData] = await Promise.all([
                getAdminEmailByUuid(item.uuid),
                getAdminEmailEvents({
                    page: 1,
                    page_size: 100,
                    client_email: item.email || appliedQuery.email || undefined,
                    message_id: item.message_id || undefined,
                    date_from: appliedQuery.dateFrom || undefined,
                    date_to: appliedQuery.dateTo || undefined,
                }),
            ]);
            setDetailContent(fresh);
            setDetailEvents(Array.isArray(eventsData?.items) ? eventsData.items : []);
        } catch (error) {
            if (error?.response?.status === 404) {
                setDetailError(t(`${prefix}.errors.notFound`));
            } else {
                setDetailError(resolveApiError(error, t(`${prefix}.errors.detailLoad`)));
            }
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setShowDetail(false);
        setDetailContent(null);
        setDetailEvents([]);
        setDetailError(null);
    };

    const renderEventLabel = (event) => {
        const labelKey = brevoEventToLabelKey(event);
        return labelKey ? t(`${prefix}.events.${labelKey}`) : (event || '—');
    };

    const renderStatCard = (label, value, variant = 'light', subValue = null) => (
        <Col xs={6} md={4} lg={2} className="mb-3">
            <Card className="email-monitor-stat-card h-100">
                <Card.Body className="py-3">
                    <div className="text-muted small mb-1">{label}</div>
                    {statsLoading ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <>
                            <div className="fw-semibold fs-5">
                                {value ?? '—'}
                                {variant !== 'light' && value != null && (
                                    <Badge bg={variant} className="ms-2 align-text-top">
                                        {variant === 'success' ? '✓' : '!'}
                                    </Badge>
                                )}
                            </div>
                            {subValue && <div className="small text-muted">{subValue}</div>}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );

    return (
        <div className="email-monitor-tab">
            <Card className="mb-3">
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h6 className="mb-0">{t(`${prefix}.statsTitle`)}</h6>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={statsLoading || listLoading}
                    >
                        <FiRefreshCw className={`me-1 ${statsLoading || listLoading ? 'spinning' : ''}`} />
                        {t('common.refresh')}
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleApplyFilters} className="mb-3">
                        <Row className="g-2 align-items-end">
                            <Col md={3}>
                                <Form.Label className="small mb-1">{t(`${prefix}.dateFrom`)}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small mb-1">{t(`${prefix}.dateTo`)}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </Col>
                            <Col md="auto">
                                <Button type="submit" variant="primary" size="sm">
                                    {t(`${prefix}.applyDates`)}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {statsError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setStatsError(null)}>
                            {statsError}
                        </Alert>
                    )}

                    <Row>
                        {renderStatCard(t(`${prefix}.kpi.requests`), stats?.requests)}
                        {renderStatCard(t(`${prefix}.kpi.delivered`), stats?.delivered, 'success')}
                        {renderStatCard(
                            t(`${prefix}.kpi.opens`),
                            stats?.opens,
                            'primary',
                            stats?.unique_opens != null
                                ? t(`${prefix}.kpi.uniqueOpens`, { count: stats.unique_opens })
                                : null
                        )}
                        {renderStatCard(
                            t(`${prefix}.kpi.clicks`),
                            stats?.clicks,
                            'primary',
                            stats?.unique_clicks != null
                                ? t(`${prefix}.kpi.uniqueClicks`, { count: stats.unique_clicks })
                                : null
                        )}
                        {renderStatCard(
                            t(`${prefix}.kpi.bounces`),
                            (stats?.soft_bounces ?? 0) + (stats?.hard_bounces ?? 0),
                            'warning',
                            t(`${prefix}.kpi.bouncesDetail`, {
                                soft: stats?.soft_bounces ?? 0,
                                hard: stats?.hard_bounces ?? 0,
                            })
                        )}
                        {renderStatCard(
                            t(`${prefix}.kpi.spamUnsub`),
                            (stats?.spam_reports ?? 0) + (stats?.unsubscribed ?? 0),
                            'danger'
                        )}
                    </Row>
                    <p className="text-muted small mb-0">{t(`${prefix}.statsHint`)}</p>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h6 className="mb-0">{t(`${prefix}.listTitle`)}</h6>
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={exportLoading || listLoading || statsLoading}
                    >
                        {exportLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t(`${prefix}.exportExcelLoading`)}
                            </>
                        ) : (
                            <>
                                <FiDownload className="me-1" />
                                {t(`${prefix}.exportExcel`)}
                            </>
                        )}
                    </Button>
                </Card.Header>
                <Card.Body>
                    {exportError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setExportError(null)}>
                            {exportError}
                        </Alert>
                    )}

                    <Form onSubmit={handleApplyFilters} className="mb-3">
                        <Row className="g-2">
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.eventType`)}</Form.Label>
                                <Form.Select
                                    value={eventType}
                                    onChange={(e) => setEventType(e.target.value)}
                                    disabled={Boolean(subject.trim())}
                                >
                                    {EMAIL_MONITOR_EVENT_TYPES.map(({ value, labelKey }) => (
                                        <option key={value || 'all'} value={value}>
                                            {t(`${prefix}.events.${labelKey}`)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small mb-1">{t(`${prefix}.clientEmail`)}</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="client@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.subject`)}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t(`${prefix}.subjectPlaceholder`)}
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    disabled={Boolean(eventType)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.templateId`)}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    placeholder="12"
                                    value={templateId}
                                    onChange={(e) => setTemplateId(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small mb-1">{t(`${prefix}.messageId`)}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="&lt;...@smtp-relay.brevo.com&gt;"
                                    value={messageId}
                                    onChange={(e) => setMessageId(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.pageSize`)}</Form.Label>
                                <Form.Select
                                    value={appliedQuery.pageSize}
                                    onChange={(e) => {
                                        const nextSize = Number(e.target.value);
                                        setAppliedQuery((prev) => ({
                                            ...prev,
                                            pageSize: nextSize,
                                            page: 1,
                                        }));
                                    }}
                                >
                                    {EMAIL_MONITOR_PAGE_SIZES.map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={12} className="d-flex align-items-end gap-2">
                                <Button type="submit" variant="primary" size="sm">
                                    {t('common.search')}
                                </Button>
                                <Button type="button" variant="outline-secondary" size="sm" onClick={handleResetFilters}>
                                    {t(`${prefix}.reset`)}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {filterValidationError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setFilterValidationError(null)}>
                            {filterValidationError}
                        </Alert>
                    )}

                    {isSubjectFilterMode && (
                        <Alert variant="info" className="py-2 mb-3">
                            {t(`${prefix}.subjectFilterHint`)}
                        </Alert>
                    )}

                    {listError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setListError(null)}>
                            {listError}
                        </Alert>
                    )}

                    <div className="table-responsive email-monitor-table-wrap">
                        <Table striped bordered hover size="sm" className="admin-table mb-0">
                            <thead>
                                <tr>
                                    {isEventListMode ? (
                                        <>
                                            <th>{t(`${prefix}.columns.date`)}</th>
                                            <th>{t(`${prefix}.columns.email`)}</th>
                                            <th>{t(`${prefix}.columns.event`)}</th>
                                            <th>{t(`${prefix}.columns.tag`)}</th>
                                            <th>{t(`${prefix}.columns.template`)}</th>
                                            <th>{t(`${prefix}.columns.reason`)}</th>
                                        </>
                                    ) : (
                                        <>
                                            <th>{t(`${prefix}.columns.uuid`)}</th>
                                            <th>{t(`${prefix}.columns.date`)}</th>
                                            <th>{t(`${prefix}.columns.email`)}</th>
                                            <th>{t(`${prefix}.columns.subject`)}</th>
                                            <th>{t(`${prefix}.columns.template`)}</th>
                                            <th>{t(`${prefix}.columns.messageId`)}</th>
                                            <th>{t(`${prefix}.columns.actions`)}</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {listLoading ? (
                                    <tr>
                                        <td colSpan={isEventListMode ? 6 : 7} className="text-center py-4">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            {t('common.loading')}
                                        </td>
                                    </tr>
                                ) : items.length === 0 ? (
                                    <tr>
                                        <td colSpan={isEventListMode ? 6 : 7} className="text-center text-muted py-4">
                                            {t(`${prefix}.noData`)}
                                        </td>
                                    </tr>
                                ) : isEventListMode ? (
                                    items.map((item, index) => (
                                        <tr key={`${item.message_id}-${item.event}-${item.date}-${index}`}>
                                            <td className="text-nowrap small">
                                                {formatEmailMonitorDate(item.date)}
                                            </td>
                                            <td className="small">{item.email || '—'}</td>
                                            <td>
                                                <Badge bg={getEmailEventBadgeVariant(item.event)}>
                                                    {renderEventLabel(item.event)}
                                                </Badge>
                                            </td>
                                            <td className="small">{item.tag || '—'}</td>
                                            <td>
                                                {item.template_id != null ? (
                                                    <Badge bg="secondary">{item.template_id}</Badge>
                                                ) : '—'}
                                            </td>
                                            <td className="small" title={item.reason || ''}>
                                                {item.reason || '—'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    items.map((item) => (
                                        <tr key={item.uuid || item.message_id}>
                                            <td>
                                                {item.uuid ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0 email-uuid-copy"
                                                        onClick={() => handleCopy(item.uuid, item.uuid)}
                                                        title={item.uuid}
                                                    >
                                                        <code>{truncateEmailUuid(item.uuid)}</code>
                                                        <FiCopy className="ms-1" />
                                                        {copiedField === item.uuid && (
                                                            <span className="text-success small ms-1">✓</span>
                                                        )}
                                                    </button>
                                                ) : '—'}
                                            </td>
                                            <td className="text-nowrap small">
                                                {formatEmailMonitorDate(item.date)}
                                            </td>
                                            <td className="small">{item.email || '—'}</td>
                                            <td className="small email-subject-cell">
                                                {truncateEmailSubject(item.subject, 120)}
                                            </td>
                                            <td>
                                                {item.template_id != null ? (
                                                    <Badge bg="secondary">{item.template_id}</Badge>
                                                ) : '—'}
                                            </td>
                                            <td className="small" title={item.message_id || ''}>
                                                {truncateMessageId(item.message_id)}
                                            </td>
                                            <td>
                                                {item.uuid ? (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => openDetail(item)}
                                                        title={t(`${prefix}.detail`)}
                                                    >
                                                        <FiEye />
                                                    </Button>
                                                ) : '—'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                        <span className="text-muted small">
                            {pageRange?.total != null
                                ? t(`${prefix}.pageIndicatorTotal`, {
                                    page: appliedQuery.page,
                                    from: pageRange.from,
                                    to: pageRange.to,
                                    total: pageRange.total,
                                })
                                : t(`${prefix}.pageIndicator`, { page: appliedQuery.page })}
                        </span>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={appliedQuery.page <= 1 || listLoading}
                                onClick={() => setAppliedQuery((prev) => ({
                                    ...prev,
                                    page: Math.max(1, prev.page - 1),
                                }))}
                            >
                                {t(`${prefix}.prev`)}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={!hasNextPage || listLoading}
                                onClick={() => setAppliedQuery((prev) => ({
                                    ...prev,
                                    page: prev.page + 1,
                                }))}
                            >
                                {t(`${prefix}.next`)}
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showDetail} onHide={closeDetail} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>{t(`${prefix}.detailTitle`)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailLoading && (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}
                    {detailError && (
                        <Alert variant="danger">{detailError}</Alert>
                    )}
                    {detailContent && !detailLoading && (
                        <div className="email-detail-grid">
                            <DetailRow
                                label="UUID"
                                value={detailContent.uuid}
                                onCopy={() => handleCopy(detailContent.uuid, 'detail-uuid')}
                                copied={copiedField === 'detail-uuid'}
                            />
                            <DetailRow label={t(`${prefix}.columns.email`)} value={detailContent.email} />
                            <DetailRow label={t(`${prefix}.columns.subject`)} value={detailContent.subject} />
                            <DetailRow
                                label={t(`${prefix}.columns.messageId`)}
                                value={detailContent.message_id || '—'}
                            />

                            {detailContent.body && (
                                <DetailRow
                                    label={t(`${prefix}.htmlPreview`)}
                                    value={(
                                        <iframe
                                            title={t(`${prefix}.htmlPreview`)}
                                            sandbox=""
                                            srcDoc={detailContent.body}
                                            className="email-monitor-preview-frame"
                                        />
                                    )}
                                    full
                                />
                            )}

                            {detailEvents.length > 0 && (
                                <DetailRow
                                    label={t(`${prefix}.detailEvents`)}
                                    value={(
                                        <ul className="list-unstyled mb-0">
                                            {detailEvents.map((evt, index) => (
                                                <li key={`${evt.event}-${evt.date}-${index}`} className="email-monitor-timeline-item">
                                                    <Badge bg={getEmailEventBadgeVariant(evt.event)} className="me-2">
                                                        {renderEventLabel(evt.event)}
                                                    </Badge>
                                                    <span className="small text-muted me-2">
                                                        {formatEmailMonitorDate(evt.date)}
                                                    </span>
                                                    {evt.reason && (
                                                        <span className="small text-danger">{evt.reason}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    full
                                />
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDetail}>
                        {t('common.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const DetailRow = ({ label, value, onCopy, copied, full = false }) => (
    <div className={`email-detail-row ${full ? 'email-detail-row-full' : ''}`}>
        <div className="email-detail-label text-muted small">{label}</div>
        <div className="email-detail-value">
            {onCopy ? (
                <button type="button" className="btn btn-link btn-sm p-0 text-start" onClick={onCopy}>
                    <code>{value}</code>
                    <FiCopy className="ms-1" />
                    {copied && <span className="text-success small ms-1">✓</span>}
                </button>
            ) : (
                value ?? '—'
            )}
        </div>
    </div>
);

export default EmailMonitorTab;
